import dayjs from 'dayjs';
import { Activity as ActivityIcon } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, XAxis } from 'recharts';
import { Fragment } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyBlock } from '@/components/ui/empty-block';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from '@/components/ui/chart';
import { EscrowStatusEnum } from 'src/graphql/schema';
import { cn } from 'src/lib/utils';

const chartConfig = {
  amount: {
    label: 'Updates',
    color: 'var(--secondary)',
  },
} satisfies ChartConfig;

const ActivityChart = ({ depositActivity }: { depositActivity: Record<string, number> }) => {
  const activity = Object.entries(depositActivity).map(([date, amount], index) => ({
    date,
    amount,
    index,
  }));

  return (
    <ChartContainer config={chartConfig} className="aspect-auto h-[228px] w-full pb-6">
      <BarChart accessibilityLayer data={activity} barSize={10}>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="date"
          tickLine={false}
          tickMargin={10}
          axisLine={false}
          interval={0}
          tickFormatter={(value) => dayjs(value).get('date').toString()}
        />
        <ChartTooltip
          cursor={false}
          content={<ChartTooltipContent labelFormatter={(value) => dayjs(value).format('DD MMM YYYY')} />}
        />
        <Bar dataKey="amount" fill="var(--color-amount)" radius={10} />
        <ChartLegend content={<ChartLegendContent className="justify-start" />} />
      </BarChart>
    </ChartContainer>
  );
};

const CardWrapper = ({
  children,
  classes: { header, content },
  status,
}: React.PropsWithChildren<{ classes: { header?: string; content?: string }; status: EscrowStatusEnum }>) => {
  return (
    <Card className={cn(status === EscrowStatusEnum.Terminated && 'opacity-70 pointer-events-none')}>
      <CardHeader className={header}>
        <CardTitle className="text-lg">Deposit activity</CardTitle>
      </CardHeader>
      <CardContent className={content}>{children}</CardContent>
    </Card>
  );
};

const ActivityContainer = () => {
  const {
    softwareEscrow: { depositActivity },
  } = useSoftwareEscrow();

  const totalActivityUpdates = Object.values(depositActivity).reduce(
    (acc: number, amount: unknown) => acc + Number(amount),
    0,
  );

  return (
    <Fragment>
      <div className="flex items-end gap-1">
        <div className="text-4xl font-semibold text-foreground">{totalActivityUpdates}</div>
        <span className="text-muted-foreground text-sm">updates this month</span>
      </div>
      <ActivityChart depositActivity={depositActivity} />
    </Fragment>
  );
};

const Activity = () => {
  const {
    softwareEscrow: { status, activatedAt },
  } = useSoftwareEscrow();

  if (activatedAt)
    return (
      <CardWrapper classes={{ header: 'pb-4', content: 'pt-6 space-y-6' }} status={status}>
        <ActivityContainer />
      </CardWrapper>
    );

  return (
    <CardWrapper classes={{ header: 'pb-3' }} status={status}>
      <EmptyBlock
        withCard={false}
        classes={{
          content: 'flex flex-col justify-center min-h-[160px]',
          title: 'text-base leading-[16px]',
          description: 'leading-tight',
        }}
        title="No activity yet"
        description="The deposit activity will be logged here once the escrow is active."
        icon={ActivityIcon}
      />
    </CardWrapper>
  );
};

export default Activity;
